"use client";

import type { OnboardingWizardStep } from "@components/OnboardingWizard/types";
import { trackEvent } from "@utils/tracking";
import { Track } from "@/src/lib/tracking";

export type OnboardingAnalyticsParams = Record<
  string,
  string | number | boolean | string[] | undefined
>;

export type OnboardingLegacyEvent = {
  event: string;
  params?: OnboardingAnalyticsParams;
};

const SESSION_PREFIX = "onboarding_analytics:";

/**
 * GA 用的参数 —— 数组**原样传**。
 *
 * Firebase 的 `processAnalyticsParams` 会把数组 `JSON.stringify` 成 `["a","b"]`，
 * 这是 GA 侧现有的数据格式。这里若先 join 成 `a,b` 就是单方面改了 GA 口径。
 */
function normalizeParams(
  params?: OnboardingAnalyticsParams,
): Record<string, string | number | boolean | string[]> | undefined {
  if (!params) {
    return undefined;
  }
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number | boolean | string[]] =>
      entry[1] !== undefined,
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/**
 * OpenReplay 用的参数 —— 必须扁平标量（规范 §3.2），数组拍平。
 *
 * `xxx: ["a","b"]` → `xxx: "a,b"` + `xxx_count: 2`。
 * 带上个数是因为 join 后的串没法直接聚合，而"选了几个"往往才是要看的指标。
 */
function toTrackParams(
  params?: OnboardingAnalyticsParams,
): Record<string, string | number | boolean> | undefined {
  if (!params) {
    return undefined;
  }
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) {
      continue;
    }
    if (Array.isArray(v)) {
      out[k] = v.join(",");
      out[`${k}_count`] = v.length;
      continue;
    }
    out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * 曝光类事件名后缀 —— 决定 `event_type` 是 `view` 还是 `action`。
 *
 * 用后缀而不是逐个调用点传参：onboarding 的事件名后缀本来就严格遵守这个约定
 * （`_view` / `_shown` 结尾的全是曝光），逐个改 40 多个调用点只会引入手误。
 * 不符合约定的用 [trackOnboardingAnalytics] 的 `eventType` 显式覆盖。
 */
const VIEW_EVENT_SUFFIX = /(_view|_shown|_view_plans)$/;

/**
 * PRD 事件 + 可选 legacy 双写（V3.0.1 过渡期）。
 *
 * **通道分工**（规范 §0）：
 * - GA：PRD 名 + legacy 名都发（存量口径，冻结不动）
 * - OpenReplay：**只发 PRD 名**。两个名指同一件事，都发就是一次动作两条记录（§5）
 */
export function trackOnboardingAnalytics(
  prdEvent: string,
  params?: OnboardingAnalyticsParams,
  legacy?: OnboardingLegacyEvent,
  eventType?: "view" | "action",
  options?: { declarativeView?: boolean },
): void {
  const normalized = normalizeParams(params);
  trackEvent(prdEvent, normalized);
  if (legacy) {
    trackEvent(legacy.event, normalizeParams(legacy.params ?? params));
  }
  const type =
    eventType ?? (VIEW_EVENT_SUFFIX.test(prdEvent) ? "view" : "action");
  if (type === "view" && options?.declarativeView) {
    return;
  }
  const trackParams = toTrackParams(params);
  if (type === "view") {
    Track.view(prdEvent, trackParams);
  } else {
    Track.event(prdEvent, trackParams);
  }
}

export function trackOnboardingViewOnce(
  sessionKey: string,
  prdEvent: string,
  params?: OnboardingAnalyticsParams,
  legacy?: OnboardingLegacyEvent,
): void {
  if (typeof window === "undefined") {
    return;
  }
  const storageKey = `${SESSION_PREFIX}${sessionKey}`;
  if (sessionStorage.getItem(storageKey)) {
    return;
  }
  sessionStorage.setItem(storageKey, "1");
  // once 语义的都是曝光类，显式传 view —— 不依赖后缀（如 `ob_carousel_view` 之外可能有例外）
  trackOnboardingAnalytics(prdEvent, params, legacy, "view");
}

const QUESTION_STEP_IDS: Partial<Record<OnboardingWizardStep, string>> = {
  "quiz/name": "name",
  "quiz/age": "age",
  "quiz/income": "income",
  "quiz/expenses": "expense",
  "quiz/cash": "cash",
  "quiz/debt": "debt",
  "quiz/invest": "invest",
  "quiz/risk": "risk",
};

export function trackObQuestionAnswered(
  completedStep: OnboardingWizardStep,
): void {
  const step = QUESTION_STEP_IDS[completedStep];
  if (!step) {
    return;
  }
  trackOnboardingAnalytics("ob_question_answered", { step });
}

/** 问诊题曝光（name/age/income/expense/cash/debt/invest/risk） */
export function trackObQuestionView(viewedStep: OnboardingWizardStep): void {
  const step = QUESTION_STEP_IDS[viewedStep];
  if (!step) {
    return;
  }
  Track.view("ob_question_view", { step });
  trackOnboardingAnalytics("ob_question_view", { step });
}

export function trackObValuePropView(): void {
  Track.view("ob_value_prop_view");
  trackOnboardingAnalytics("ob_value_prop_view");
}

export function trackObCarouselView(): void {
  trackOnboardingViewOnce("ob_carousel_view", "ob_carousel_view");
}

export function trackAuthLandingView(platform: string): void {
  trackOnboardingViewOnce(
    `auth_landing_view:${platform}`,
    "auth_landing_view",
    { platform },
  );
}

export function trackAuthSigninTap(params: {
  platform: string;
  entry: string;
  slide_index: number;
  method: string;
}): void {
  trackOnboardingAnalytics("auth_signin_tap", params);
}

export function trackAuthSigninPageEnter(params: {
  platform: string;
  slide_index: number;
  button: string;
}): void {
  trackOnboardingAnalytics("auth_signin_page_enter", params);
}

export function trackAuthSigninSuccess(params: {
  platform: string;
  method: string;
  entry: string;
  landing_id?: string;
  ad_source?: string;
}): void {
  trackOnboardingAnalytics("auth_signin_success", params);
}

export function trackObLoginSuccess(params: {
  auth_method: string;
  user_id?: string;
}): void {
  trackOnboardingAnalytics("ob_login_success", params, {
    event: "ob_funnel_auth_success",
    params,
  });
}

export function trackObRestart(lastStep: string): void {
  trackOnboardingAnalytics("ob_restart", { last_step: lastStep });
}

/**
 * @param band 仅广告支（`ai_job_risk`）传：PRD §1.2 的「goal 激活比例在未付费侧的漏损归因」
 *   要按 A/B/C 三支分开看，没有它这个指标算不出来。`ob_paywall_view` 被 content_unlock /
 *   tracker 等多处复用，其余入口不带，桶不会被污染。
 */
/**
 * 点任意一屏的返回入口：`onb_step_back_tap {from_step, to_step}`。
 *
 * 回溯 PRD §1.2 的「问诊完成比例漏损归因 / 改答率」。两个参数取 `quiz-screens.json` 的
 * stepId —— 那是两端共享的真源，native 上报同一套字面量，漏斗可合并看。
 */
export function trackOnbStepBack(fromStep: string, toStep: string): void {
  trackOnboardingAnalytics("onb_step_back_tap", {
    from_step: fromStep,
    to_step: toStep,
  });
}

export function trackObPaywallView(
  source: string,
  band?: string | null,
  options?: { declarativeView?: boolean },
): void {
  if (source === "goal_activate") {
    trackOnboardingAnalytics("paywall2_view", { source: "put_ed_to_work" });
    return;
  }
  if (source === "onboarding_plan") {
    trackOnboardingAnalytics("paywall_show", { feature: "onboarding_plan" });
  }
  const params = band ? { source, band } : { source };
  trackOnboardingAnalytics(
    "ob_paywall_view",
    params,
    {
      event: "ob_funnel_paywall_view",
      params,
    },
    "view",
    options,
  );
}

export function trackOnboardingResultView(params: {
  problem: string;
  data_state: "estimate";
  cohort_group?: string;
  goal?: string;
}): void {
  trackOnboardingViewOnce(
    `onboarding_result_view:${params.problem}`,
    "onboarding_result_view",
    params,
    { event: "ob_result_view", params: { goal: params.goal } },
  );
}

export function trackObPaywallDismiss(source: string): void {
  if (source === "goal_activate") {
    trackOnboardingAnalytics("paywall2_dismiss");
    return;
  }
  trackOnboardingAnalytics("ob_paywall_dismiss", { source });
}

export function trackObPaywallTrialStart(subscriptionId = ""): void {
  trackOnboardingAnalytics(
    "ob_paywall_trial_start",
    { subscription_id: subscriptionId },
    {
      event: "ob_funnel_trial_started",
      params: { subscription_id: subscriptionId },
    },
  );
}

export function trackObPaywallPurchaseSuccess(
  source: string,
  subscriptionId = "",
): void {
  if (source === "goal_activate") {
    trackOnboardingAnalytics("paywall2_purchase_success", {
      subscription_id: subscriptionId,
    });
    return;
  }
  trackOnboardingAnalytics("ob_paywall_purchase_success", { source });
}

export function trackGiftModalView(): void {
  trackOnboardingViewOnce("gift_modal_view", "gift_modal_view", undefined, {
    event: "ob_funnel_gift_set_goal_paywall_view",
  });
}

export function trackGiftSetupStart(): void {
  trackOnboardingAnalytics("gift_setup_start", undefined, {
    event: "ob_funnel_gift_set_goal_unlock_click",
  });
}

export function trackGiftGoalSetupDone(): void {
  trackOnboardingViewOnce("gift_goal_setup_done", "gift_goal_setup_done");
}

export type PutEdToWorkSource = "put_ed_to_work" | "plan";

export function trackPutEdToWorkTap(source: PutEdToWorkSource): void {
  trackOnboardingAnalytics("put_ed_to_work_tap", { source });
}

export function trackGoalLiveCardTap(): void {
  trackOnboardingAnalytics("goal_live_card_tap");
}

export function trackChatBriefCardTap(): void {
  trackOnboardingAnalytics("chat_brief_card_tap");
}
